import {
  BOSS_GRID_CELL,
  DEFAULT_ENCOUNTER_ZONES,
  EncounterZone,
  GridCell,
  OVERWORLD_ZONE_RADIUS,
  REST_GRID_CELLS,
  generateEncounterZones,
  getGridCellCenter,
} from "../data/encounterZones";
import { generateBossEncounter } from "./encounterGeneration";
import {
  RunState,
  completeEncounter,
  isAtMaxFloor,
  isFloorComplete,
  markRunVictory,
  setBossEncounter,
  clearBossEncounter,
} from "./runState";
import { OverworldState, OverworldZoneState, OverworldFlowState } from "./overworldState";
import { createSeededRng } from "../utils/rng";

export type OverworldNavigationRequest =
  | {
      kind: "startEncounter";
      enemyConfig: EncounterZone["enemyConfig"];
      overworldState: OverworldState;
      isBossEncounter: boolean;
    }
  | {
      kind: "rest";
      overworldState: OverworldState;
    };

export type OverworldBounds = { width: number; height: number };
export type OverworldPosition = { x: number; y: number };

export type OverworldZoneEntry = {
  zone: OverworldZoneState;
  navigation: OverworldNavigationRequest;
};

const BOSS_ZONE_ID = 10_000;

const createBossZone = (run: RunState, bounds: OverworldBounds): OverworldZoneState => {
  const { x, y } = getGridCellCenter(
    bounds.width,
    bounds.height,
    BOSS_GRID_CELL.row,
    BOSS_GRID_CELL.col
  );
  return {
    id: BOSS_ZONE_ID,
    x,
    y,
    radius: OVERWORLD_ZONE_RADIUS,
    kind: "boss",
    status: "available", // Boss is always available from the start
    enemyConfig: generateBossEncounter(run) ?? undefined,
  };
};

const createRestZone = (id: number, x: number, y: number): OverworldZoneState => {
  return {
    id,
    x,
    y,
    radius: OVERWORLD_ZONE_RADIUS,
    kind: "rest",
    status: "available",
  };
};

const isWithinZone = (
  zone: OverworldZoneState,
  playerPosition: OverworldPosition,
  playerRadius: number
): boolean => {
  const distance = Math.sqrt(
    (playerPosition.x - zone.x) ** 2 + (playerPosition.y - zone.y) ** 2
  );
  return distance <= zone.radius + playerRadius;
};

const pickRestCell = (run: RunState): GridCell => {
  const baseSeed = run.seed ?? Date.now();
  const floorSalt = run.floor ? run.floor * 100_000 : 0;
  const rng = createSeededRng(baseSeed + floorSalt + 50_000);
  return REST_GRID_CELLS[rng.nextInt(0, REST_GRID_CELLS.length)];
};

const mapEncounterZones = (
  run: RunState,
  bounds: OverworldBounds,
  completedZoneIds: Set<number>,
  reservedCells: GridCell[]
): OverworldZoneState[] => {
  const zones =
    run.currentFloorConfig && run.currentFloorEncounters.length > 0
      ? generateEncounterZones(run.currentFloorConfig, bounds.width, bounds.height, {
          seed: run.seed,
          floor: run.floor,
          encounterCount: run.currentFloorEncounters.length,
          reservedCells,
        }).map((zone, index) => ({
          ...zone,
          enemyConfig: run.currentFloorEncounters[index],
        }))
      : DEFAULT_ENCOUNTER_ZONES.map((zone, index) => ({
          ...zone,
          enemyConfig: run.currentFloorEncounters[index],
        }));

  const encounterZones: OverworldZoneState[] = zones.map((zone) => ({
    ...zone,
    kind: "encounter",
    status: completedZoneIds.has(zone.id) ? "completed" : "available",
  }));

  return encounterZones;
};

export const ensureOverworldMap = (
  overworldState: OverworldState,
  bounds: OverworldBounds
): void => {
  overworldState.worldBounds = bounds;

  const needsRebuild =
    overworldState.zones.length === 0 ||
    overworldState.generatedForFloor !== overworldState.run.floor;

  if (needsRebuild) {
    overworldState.completedZoneIds = new Set();
    overworldState.lastVisitedZoneId = undefined;
    const restCell = pickRestCell(overworldState.run);
    const reservedCells = [BOSS_GRID_CELL, restCell];

    // Generate combat encounter zones
    const encounterZones = mapEncounterZones(
      overworldState.run,
      bounds,
      overworldState.completedZoneIds,
      reservedCells
    );

    // Generate rest zone (1 per floor)
    const restZoneId = 5000; // Use 5000-series IDs for rest zones
    const restPosition = getGridCellCenter(
      bounds.width,
      bounds.height,
      restCell.row,
      restCell.col
    );
    const restZone = createRestZone(restZoneId, restPosition.x, restPosition.y);

    // Combine all zones: encounters + rest + boss
    overworldState.zones = [...encounterZones, restZone, createBossZone(overworldState.run, bounds)];
    overworldState.generatedForFloor = overworldState.run.floor;
    overworldState.flow = "exploring";
    overworldState.activeZoneId = undefined;
    return;
  }

  // Keep zone status in sync with completed set when reloading the scene
  overworldState.zones = overworldState.zones.map((zone) =>
    (zone.kind === "encounter" || zone.kind === "rest") && overworldState.completedZoneIds.has(zone.id)
      ? { ...zone, status: "completed" as const }
      : zone
  );
};

export const checkOverworldZoneEntry = (
  overworldState: OverworldState,
  playerPosition: OverworldPosition,
  playerRadius: number
): OverworldZoneEntry | null => {
  const availableZone = overworldState.zones.find(
    (zone) => zone.status === "available" && isWithinZone(zone, playerPosition, playerRadius)
  );

  if (availableZone && availableZone.id !== overworldState.lastVisitedZoneId) {
    const navigation = prepareZoneEncounter(overworldState, availableZone.id);
    if (navigation) {
      overworldState.playerX = playerPosition.x;
      overworldState.playerY = playerPosition.y;
      overworldState.lastVisitedZoneId = availableZone.id;
      return { zone: availableZone, navigation };
    }
  }

  const inAnyZone = overworldState.zones.some((zone) =>
    isWithinZone(zone, playerPosition, playerRadius)
  );
  if (!inAnyZone) {
    overworldState.lastVisitedZoneId = undefined;
  }

  return null;
};

export const prepareZoneEncounter = (
  overworldState: OverworldState,
  zoneId: number
): OverworldNavigationRequest | null => {
  const zone = overworldState.zones.find((candidate) => candidate.id === zoneId);
  if (!zone || zone.status !== "available") {
    return null;
  }

  overworldState.activeZoneId = zoneId;

  // Handle rest zones separately (no combat)
  if (zone.kind === "rest") {
    return {
      kind: "rest",
      overworldState,
    };
  }

  // Handle combat encounters (regular and boss)
  const isBossEncounter = zone.kind === "boss";
  if (isBossEncounter) {
    setBossEncounter(overworldState.run);
  }

  return {
    kind: "startEncounter",
    enemyConfig: zone.enemyConfig,
    overworldState,
    isBossEncounter,
  };
};

export const markZoneCompleted = (overworldState: OverworldState, zoneId?: number): void => {
  const resolvedId = zoneId ?? overworldState.activeZoneId;
  if (resolvedId === undefined) {
    return;
  }

  const zone = overworldState.zones.find((candidate) => candidate.id === resolvedId);
  if (!zone) {
    return;
  }

  zone.status = "completed";
  overworldState.completedZoneIds.add(zone.id);
};

export const applyEncounterVictory = (
  overworldState: OverworldState,
  options?: { wasBossEncounter?: boolean; zoneId?: number }
): OverworldFlowState => {
  const resolvedId = options?.zoneId ?? overworldState.activeZoneId;
  const zone =
    resolvedId === undefined
      ? undefined
      : overworldState.zones.find((candidate) => candidate.id === resolvedId);
  const wasBossEncounter = options?.wasBossEncounter ?? zone?.kind === "boss";

  markZoneCompleted(overworldState, resolvedId);

  if (!wasBossEncounter) {
    completeEncounter(overworldState.run);
    overworldState.flow = isFloorComplete(overworldState.run) ? "floorVictory" : "encounterVictory";
    // Boss is always available - no unlock needed
    return overworldState.flow;
  }

  // Boss victory
  clearBossEncounter(overworldState.run);
  overworldState.flow = "floorVictory";

  if (isAtMaxFloor(overworldState.run)) {
    markRunVictory(overworldState.run);
  }

  return overworldState.flow;
};

export const resetOverworldAfterReward = (overworldState: OverworldState): void => {
  overworldState.flow = "exploring";
  overworldState.activeZoneId = undefined;
};

export const refreshZonesForNewFloor = (overworldState: OverworldState): void => {
  overworldState.completedZoneIds = new Set();
  overworldState.zones = [];
  overworldState.activeZoneId = undefined;
  overworldState.flow = "exploring";

  if (overworldState.worldBounds) {
    ensureOverworldMap(overworldState, overworldState.worldBounds);
  }
};
